import { useCallback, useEffect, useState } from 'react'
import { archiveDocument } from '../application/archive-document'
import { listDocuments } from '../application/list-documents'
import { updateDocumentTags } from '../application/update-document-tags'
import { uploadDocument } from '../application/upload-document'
import type { VaultDocument, VaultDocumentFilter, VaultDocumentUpload } from '../domain/document'
import { vaultDocuments } from '../wiring'

const useVaultDocuments = (filter: VaultDocumentFilter) => {
	const [documents, setDocuments] = useState<VaultDocument[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [isMutating, setIsMutating] = useState(false)

	const load = useCallback(async (): Promise<void> => {
		setIsLoading(true)
		try {
			const result = await listDocuments(vaultDocuments, filter)
			setDocuments(result)
		} finally {
			setIsLoading(false)
		}
	}, [filter])

	useEffect(() => {
		void load()
	}, [load])

	const upload = async (input: VaultDocumentUpload): Promise<void> => {
		setIsMutating(true)
		try {
			await uploadDocument(vaultDocuments, input)
			await load()
		} finally {
			setIsMutating(false)
		}
	}

	const updateTags = async (documentId: string, tags: string[]): Promise<void> => {
		setIsMutating(true)
		try {
			const next = await updateDocumentTags(vaultDocuments, documentId, tags)
			setDocuments(current => current.map(document => (document.id === documentId ? next : document)))
		} finally {
			setIsMutating(false)
		}
	}

	const archive = async (documentId: string): Promise<void> => {
		setIsMutating(true)
		try {
			await archiveDocument(vaultDocuments, documentId)
			setDocuments(current => current.filter(document => document.id !== documentId))
		} finally {
			setIsMutating(false)
		}
	}

	return { documents, isLoading, isMutating, load, upload, updateTags, archive }
}

export { useVaultDocuments }
